import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, LinearProgress,
  Paper, Radio, RadioGroup, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { api, ApiError } from '../api/client';
import type { PendingUser } from '../api/types';
import { PageHeader, shortAddr } from '../components/common';

type KeyType = 'TYPE1' | 'TYPE2';

export function AdminSignups() {
  const qc = useQueryClient();
  const [msg, setMsg] = useState<{ sev: 'success' | 'error'; text: string } | null>(null);
  const [target, setTarget] = useState<PendingUser | null>(null);
  const [keyType, setKeyType] = useState<KeyType>('TYPE2');
  const [password, setPassword] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-signups'],
    queryFn: () => api.get<PendingUser[]>('/admin/signups?status=pending'),
  });

  const open = (u: PendingUser) => {
    setTarget(u);
    // 사용자가 고른 방식을 기본값으로 둔다 — 관리자가 임의로 바꾸는 것은 예외여야 한다
    setKeyType(u.requestedKeyType ?? 'TYPE2');
    setPassword('');
    setMsg(null);
  };

  const approve = useMutation({
    mutationFn: () =>
      api.post(`/admin/signups/${target!.userUid}/approve`, {
        roles: ['user'],
        keyType,
        password: keyType === 'TYPE1' ? password : undefined,
      }),
    onSuccess: () => {
      setMsg({
        sev: 'success',
        text: keyType === 'TYPE1'
          ? '승인 완료 — KeyStore는 봉인 보관되었습니다. 사용자가 마이페이지에서 1회 내려받습니다.'
          : '승인 완료 — KMS 키 생성·온체인 등록 오케스트레이션 수행됨.',
      });
      setTarget(null);
      void qc.invalidateQueries({ queryKey: ['admin-signups'] });
    },
    onError: (e) => setMsg({ sev: 'error', text: e instanceof ApiError ? e.message : '승인 실패' }),
  });

  const mismatch = !!target?.requestedKeyType && target.requestedKeyType !== keyType;

  return (
    <Box>
      <PageHeader crumb="관리자 · 가입 승인" title="가입 승인 · 권한 부여"
        desc="승인 시 KMS 키 생성 → 온체인ID 클레임 등록 → Identity Registry 등록이 자동 오케스트레이션됩니다." />
      {msg && <Alert severity={msg.sev} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      {isLoading && <LinearProgress />}
      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>신청자(uid)</TableCell>
              <TableCell>국가</TableCell>
              <TableCell>신청 키 유형</TableCell>
              <TableCell>신청일</TableCell>
              <TableCell align="right">조치</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data ?? []).map((u) => (
              <TableRow key={u.userUid} hover>
                <TableCell>
                  <b>{u.loginId}</b>{' '}
                  <Typography component="span" variant="caption" sx={{ fontFamily: 'monospace' }}>uid {shortAddr(u.userUid)}</Typography>
                </TableCell>
                <TableCell>{u.countryCode}</TableCell>
                <TableCell><KeyTypeChip type={u.requestedKeyType} /></TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{new Date(u.createdAt).toLocaleDateString()}</TableCell>
                <TableCell align="right">
                  <Button size="small" variant="contained" onClick={() => open(u)}>승인</Button>
                </TableCell>
              </TableRow>
            ))}
            {data?.length === 0 && (
              <TableRow><TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 4 }}>승인 대기 중인 신청이 없습니다.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={!!target} onClose={() => setTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>가입 승인 — {target?.loginId}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {msg?.sev === 'error' && <Alert severity="error">{msg.text}</Alert>}

            <Box>
              <Typography variant="body2" color="text.secondary" mb={1}>
                키 관리 방식 — 신청자가 선택한 값이 기본으로 잡힙니다.
              </Typography>
              <RadioGroup value={keyType} onChange={(e) => setKeyType(e.target.value as KeyType)}>
                <FormControlLabel
                  value="TYPE2"
                  control={<Radio />}
                  label={<>Type2 · 위탁 관리 <Typography component="span" variant="caption" color="text.secondary">— 개인키를 KMS에 보관하고 서비스가 서명을 대행합니다</Typography></>}
                />
                <FormControlLabel
                  value="TYPE1"
                  control={<Radio />}
                  label={<>Type1 · 직접 관리 <Typography component="span" variant="caption" color="text.secondary">— KeyStore를 사용자에게 1회 인도하고 개인키는 즉시 파기됩니다</Typography></>}
                />
              </RadioGroup>
            </Box>

            {mismatch && (
              <Alert severity="warning">
                신청자는 <b>{target?.requestedKeyType}</b>을 선택했는데 <b>{keyType}</b>으로 발급하려 합니다.
                다르게 발급하면 이력에 기록됩니다.
              </Alert>
            )}

            {keyType === 'TYPE1' && (
              <>
                <TextField
                  label="KeyStore 비밀번호"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  helperText="KeyStore(V3) 암호화에 쓰입니다. 사용자에게 별도 경로로 전달해야 하며, 분실 시 복구할 수 없습니다."
                />
                <Alert severity="info">
                  KeyStore는 승인 응답에 실리지 않습니다. 봉인 보관되고 사용자가 <b>마이페이지에서 1회</b> 내려받으며,
                  내려받는 즉시 서버 사본은 파기됩니다.
                </Alert>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTarget(null)}>취소</Button>
          <Button
            variant="contained"
            onClick={() => approve.mutate()}
            disabled={approve.isPending || (keyType === 'TYPE1' && password.length < 8)}
          >
            {approve.isPending ? '승인 중…' : `승인 (${keyType === 'TYPE1' ? 'Type1' : 'Type2'})`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/** 신청 키 유형 — 예전 가입분은 값이 없어 "미지정"으로 표시된다 */
function KeyTypeChip({ type }: { type?: KeyType | null }) {
  if (!type) return <Chip size="small" label="미지정" sx={{ fontWeight: 700 }} />;
  return type === 'TYPE1'
    ? <Chip size="small" color="warning" label="Type1 · 직접 관리" sx={{ fontWeight: 700 }} />
    : <Chip size="small" color="info" label="Type2 · 위탁" sx={{ fontWeight: 700 }} />;
}
